import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "list",
    "newForm",
    "newContent",
    "newSubmitButton",
    "uploadTab",
    "manualTab",
    "uploadTabButton",
    "manualTabButton",
    "uploadSubmitButton",
    "audioBlobId",
    "sessionDate",
    "audioToken",
    "audioContent"
  ]

  static values = {
    createUrl: String
  }

  connect() {
    this.uploadInProgress = false
  }

  showUploadTab() {
    if (this.hasUploadTabTarget && this.hasManualTabTarget) {
      this.uploadTabTarget.classList.remove("hidden")
      this.manualTabTarget.classList.add("hidden")
      this.uploadTabButtonTarget.classList.add("text-indigo-600", "border-b-2", "border-indigo-600")
      this.uploadTabButtonTarget.classList.remove("text-gray-500")
      this.manualTabButtonTarget.classList.remove("text-indigo-600", "border-b-2", "border-indigo-600")
      this.manualTabButtonTarget.classList.add("text-gray-500")
    }
  }

  showManualTab() {
    if (this.hasUploadTabTarget && this.hasManualTabTarget) {
      this.uploadTabTarget.classList.add("hidden")
      this.manualTabTarget.classList.remove("hidden")
      this.manualTabButtonTarget.classList.add("text-indigo-600", "border-b-2", "border-indigo-600")
      this.manualTabButtonTarget.classList.remove("text-gray-500")
      this.uploadTabButtonTarget.classList.remove("text-indigo-600", "border-b-2", "border-indigo-600")
      this.uploadTabButtonTarget.classList.add("text-gray-500")
    }
  }

  enableUploadSubmit() {
    if (this.hasUploadSubmitButtonTarget) {
      this.uploadSubmitButtonTarget.disabled = false
    }
  }

  disableUploadSubmit() {
    if (this.hasUploadSubmitButtonTarget) {
      this.uploadSubmitButtonTarget.disabled = true
    }
  }

  async createWithAudio(event) {
    event.preventDefault()

    const blobSignedId = this.hasAudioBlobIdTarget ? this.audioBlobIdTarget.value : null
    const sessionDate = this.hasSessionDateTarget ? this.sessionDateTarget.value : null

    if (!blobSignedId) {
      alert('Please upload an audio file first')
      return
    }

    this.uploadSubmitButtonTarget.disabled = true
    this.uploadSubmitButtonTarget.textContent = "Creating..."

    try {
      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        body: JSON.stringify({
          transcript: {
            blob_signed_id: blobSignedId,
            session_date: sessionDate,
            content: '<p>Transcription in progress...</p>'
          }
        }),
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      if (data.success) {
        window.location.reload()
      } else {
        alert(data.errors?.join(', ') || 'Failed to create transcript')
      }
    } catch (error) {
      console.error('Error:', error)
      alert('Failed to create transcript')
    } finally {
      this.uploadSubmitButtonTarget.disabled = false
      this.uploadSubmitButtonTarget.textContent = "Upload & Transcribe"
    }
  }

  toggleNewForm() {
    if (this.hasNewFormTarget) {
      this.newFormTarget.classList.toggle("hidden")
    }
  }

  async createTranscript(event) {
    event.preventDefault()

    const content = document.getElementById('new_transcript_content')?.value
    const sessionDate = document.getElementById('manual_session_date')?.value || null

    this.newSubmitButtonTarget.disabled = true
    this.newSubmitButtonTarget.textContent = "Adding..."

    try {
      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        body: JSON.stringify({ transcript: { content, session_date: sessionDate } }),
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      if (data.success) {
        window.location.reload()
      } else {
        alert(data.errors?.join(', ') || 'Failed to create transcript')
      }
    } catch (error) {
      console.error('Error:', error)
      alert('Failed to create transcript')
    } finally {
      this.newSubmitButtonTarget.disabled = false
      this.newSubmitButtonTarget.textContent = "Add Transcript"
    }
  }

  edit(event) {
    const entry = event.target.closest('[data-transcript-entry]')
    entry.querySelector('[data-view-mode]').classList.add("hidden")
    entry.querySelector('[data-edit-mode]').classList.remove("hidden")
  }

  cancel(event) {
    const entry = event.target.closest('[data-transcript-entry]')
    entry.querySelector('[data-edit-mode]').classList.add("hidden")
    entry.querySelector('[data-view-mode]').classList.remove("hidden")
  }

  async save(event) {
    const entry = event.target.closest('[data-transcript-entry]')
    const url = entry.dataset.updateUrl
    const contentInput = entry.querySelector('[data-content-input]')
    const sessionDateInput = entry.querySelector('[data-session-date-input]')
    const saveButton = event.target

    const content = contentInput.value
    const sessionDate = sessionDateInput?.value

    saveButton.disabled = true
    saveButton.textContent = "Saving..."

    try {
      const response = await fetch(url, {
        method: 'PATCH',
        body: JSON.stringify({ transcript: { content, session_date: sessionDate } }),
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      if (data.success) {
        entry.querySelector('[data-content-display]').innerHTML = data.transcript.content || "<em class='text-gray-400'>No content</em>"
        entry.querySelector('[data-edit-mode]').classList.add("hidden")
        entry.querySelector('[data-view-mode]').classList.remove("hidden")
      } else {
        alert(data.errors?.join(', ') || 'Failed to save transcript')
      }
    } catch (error) {
      console.error('Error:', error)
      alert('Failed to save transcript')
    } finally {
      saveButton.disabled = false
      saveButton.textContent = "Save Changes"
    }
  }

  async retryTranscription(event) {
    const button = event.currentTarget
    const url = button.dataset.retryUrl

    button.disabled = true

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      if (data.success) {
        window.location.reload()
      } else {
        alert(data.error || 'Failed to retry transcription')
      }
    } catch (error) {
      console.error('Error:', error)
      alert('Failed to retry transcription')
    } finally {
      button.disabled = false
    }
  }

  handleTranscriptionReady(event) {
    const { ready } = event.detail

    if (this.hasUploadSubmitButtonTarget) {
      this.uploadSubmitButtonTarget.disabled = !ready
    }
  }

  async saveAudioTranscript(event) {
    event.preventDefault()

    const token = this.hasAudioTokenTarget ? this.audioTokenTarget.value : null
    const content = this.hasAudioContentTarget ? this.audioContentTarget.value : null
    const sessionDate = this.hasSessionDateTarget ? this.sessionDateTarget.value : null

    if (!token || !content) {
      alert('Please wait for transcription to complete')
      return
    }

    this.uploadSubmitButtonTarget.disabled = true
    this.uploadSubmitButtonTarget.innerHTML = `
      <svg class="animate-spin w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
      Saving...
    `

    try {
      const response = await fetch(this.createUrlValue, {
        method: 'POST',
        body: JSON.stringify({
          transcript: {
            content: content,
            session_date: sessionDate,
            temp_token: token
          }
        }),
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      if (data.success) {
        window.location.reload()
      } else {
        alert(data.errors?.join(', ') || 'Failed to save transcript')
      }
    } catch (error) {
      console.error('Error:', error)
      alert('Failed to save transcript')
    } finally {
      this.uploadSubmitButtonTarget.disabled = false
      this.uploadSubmitButtonTarget.innerHTML = `
        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Save Transcript
      `
    }
  }
}
